/* eslint-disable react/no-array-index-key */
/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { CaseType } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { getCaseDateWithoutDay, getCaseTime, getTimeAgo } from '@brands/Utils/formatDate';
import linkifyHtml from 'linkify-html';
import React, { useEffect, useMemo, useState } from 'react';
import { CgProfile } from 'react-icons/cg';
import { IoIosArrowDroprightCircle } from 'react-icons/io';
import sanitizeHtml from 'sanitize-html';

import { useAppSelector } from '../../hooks/useReduxHook';
import { ICase } from '../../services/cases/types/ICase';
import { IGMConversation } from '../../services/communication/types/IGMConversation';
import { IGMConversationParticipant } from '../../services/communication/types/IGMConversationParticipant';
import { getUserAvatar } from '../../services/identity/getUserAvatar';
import { UserProfile, UserRoleName } from '../../services/identity/types/UserProfile';
import { getFriendlyNameOfCaseType, getFriendlyRoleName } from '../../Utils/getFriendlyName';
import { shouldShowDoctorTitle } from '../../Utils/getProviderTitle';
import {
  hasUnreadMessageInPatientCA,
  hasUnreadMessageInPatientCACount,
  hasUnreadMessageInPatientProvider,
  hasUnreadMessageInPatientProviderCount,
  hasUnreadMessageInPrivate,
} from '../../Utils/getUnreadMessageNotification';
import styles from './case-styles.module.scss';

interface IModal {
  currentCase: ICase | undefined;
  item: IGMConversation | null;
  isSelected: boolean;
  conversationType: string;
  isMobile?: boolean;
  isArchived?: boolean;
}

const CaseMessageInfo = ({
  currentCase,
  item,
  isSelected,
  conversationType,
  isMobile,
  isArchived,
}: IModal): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const roleNavigate = useRoleNavigate();

  const unreadFromProvider = useMemo(() => {
    return currentCase ? hasUnreadMessageInPatientProviderCount(currentCase, currentCase?.provider_id) : 0;
  }, [currentCase]);

  const unreadFromCA = useMemo(() => {
    return currentCase ? hasUnreadMessageInPatientCACount(currentCase, currentCase?.care_advisor_id) : 0;
  }, [currentCase]);

  const unreadCount = unreadFromProvider + unreadFromCA;
  const isUserParticipant = item?.participants?.some((participant) => Number(participant.identity) === userInfo.id);
  const isSenderYou = Number(item?.last_message?.created_by) === Number(userInfo.id);

  // Patients don't need the Case ID; every staff role uses it to tie the
  // thread back to its case.
  const showCaseId =
    userInfo.role.name !== UserRoleName.Patient && userInfo.role.name !== UserRoleName.Dependent && !!currentCase?.id;

  const showUnreadIndicator = (participant: UserProfile): boolean => {
    if (!currentCase) return false;

    const isPatientLike =
      participant.role.name === UserRoleName.Patient || participant.role.name === UserRoleName.Dependent;

    let participantId: number | undefined;

    if (isPatientLike) {
      participantId =
        currentCase.patient.guardian_relationship === null ? currentCase.patient_id : currentCase.patient.guardian_id!;
    } else if (participant.role.name === UserRoleName.Provider) {
      participantId = currentCase.provider_id;
    } else if (participant.role.name === UserRoleName.CareAdvisor) {
      participantId = currentCase.care_advisor_id;
    } else if (participant.role.name === UserRoleName.Collector) {
      if (!currentCase.collector_id) return false;
      participantId = currentCase.collector_id;
    } else {
      return false;
    }

    if (conversationType === `${UserRoleName.Patient}_${UserRoleName.Provider}`) {
      return hasUnreadMessageInPatientProvider(currentCase, participantId);
    }
    if (conversationType === `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`) {
      return hasUnreadMessageInPatientCA(currentCase, participantId);
    }
    if (conversationType === `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`) {
      return hasUnreadMessageInPrivate(currentCase, participantId);
    }

    return false;
  };
  const [remoteParticipant, setRemoteParticipant] = useState<UserProfile[]>();
  const getRemoteParticipants = (): UserProfile[] | undefined => {
    let participants: UserProfile[] = [];
    if (currentCase?.patient) {
      participants.push(currentCase.patient);
    }
    if (currentCase?.care_advisor) {
      participants.push(currentCase.care_advisor);
    }
    if (currentCase?.provider) {
      participants.push(currentCase.provider);
    }
    if (currentCase?.collector) {
      participants.push(currentCase.collector);
    }

    if (item?.participants) {
      participants = participants.filter((participant) => participant.id !== userInfo?.id);
    }

    if (item?.participants) {
      // Resolve one display row per remote participant of the actual
      // conversation. Matching each to a current-case profile keeps live
      // threads unchanged, while an archived thread's former staff (e.g. a
      // previous care advisor no longer on the case) is synthesized from
      // the conversation's own participant record instead of dropped.
      return item.participants
        .filter(
          (participant) => Number(participant.identity) !== userInfo?.id && participant.role !== UserRoleName.Dependent,
        )
        .map((remote) => {
          const matched = participants.find((profile) =>
            profile.guardian_id ? +profile.guardian_id === +remote.identity : +profile.id === +remote.identity,
          );
          return (
            matched ??
            ({
              id: Number(remote.identity),
              full_name: remote.full_name ?? '',
              status: remote.status,
              role: { name: remote.role },
              guardian: { full_name: remote.full_name ?? '' },
            } as unknown as UserProfile)
          );
        });
    }

    return participants;
  };

  const displayLastMessageContent = (messageBody: string): string => {
    const withLinks = linkifyHtml(messageBody || '', {
      defaultProtocol: 'https',
      attributes: {
        rel: 'noopener noreferrer',
        target: '_blank',
      },
    });

    const sanitizedHtml = sanitizeHtml(withLinks, {
      allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
      allowedAttributes: {
        a: ['href', 'target', 'rel'],
        span: ['class'],
        li: ['data-list'],
      },
      allowedClasses: {
        span: ['ql-ui'],
      },
      transformTags: {
        a: (tagName, attribs) => ({
          tagName: 'a',
          attribs: {
            ...attribs,
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
      },
    });

    const match = sanitizedHtml.match(/<p[^>]*>.*?<\/p>/i);
    return match ? match[0] : sanitizedHtml;
  };

  useEffect(() => {
    if (item) {
      const remoteParticipants = getRemoteParticipants();
      setRemoteParticipant(remoteParticipants);
    }
  }, [item, currentCase]);

  if (!remoteParticipant) {
    return (
      <div className={`${styles.participantDiv} ${styles.participantMessageDiv}`}>
        <span className={styles.profileImageDate}>
          <CgProfile className={styles.emptyAvatar} />
        </span>
        <div className={styles.details}>
          <div className={styles.participantRole}>Care Advisor</div>
          <div className={styles.detailsNameImage}>
            <span className={`${styles.participantName} ${!isSelected ? styles.primaryColor : ''}`}>{}</span>
          </div>
        </div>
        <div
          className={styles.detailsIcon}
          style={{ display: isSelected ? 'none' : 'block' }}
          onClick={() => {
            roleNavigate(`/case-chat/${currentCase?.id}/${item?.sid}`);
          }}
        >
          <IoIosArrowDroprightCircle />
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.participantDiv} ${styles.participantMessageDiv}`}>
      <div className={styles.wrapDiv}>
        {remoteParticipant?.map((remoteParticipantSingle) => (
          <div
            className={`${styles.participantDivInfo} ${
              remoteParticipantSingle.status.toLowerCase() !== 'active' ? styles.inactiveParticipant : ''
            }`}
          >
            <div className={styles.participantDivInfoLeft}>
              <span className={`${styles.profileImageDate} ${styles.imageContainer}`}>
                {showUnreadIndicator(remoteParticipantSingle) && <span className={styles.unreasMessage} />}
                <img
                  alt="Avatar"
                  onError={(event: any) => {
                    event.target.src =
                      'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                    event.target.onError = null;
                  }}
                  className={`fs.exclude ${styles.profileImage}`}
                  src={getUserAvatar(
                    Number(
                      remoteParticipantSingle.role.name !== UserRoleName.Dependent
                        ? remoteParticipantSingle?.id
                        : remoteParticipantSingle.guardian_id,
                    ),
                  )}
                />
              </span>
              <div className={`${styles.details} ${isMobile && styles.mobileCaseMessageInfo}`}>
                <div className={styles.detailsNameImage}>
                  <span className={`${styles.participantName} fs.exclude ${!isSelected ? styles.primaryColor : ''}`}>
                    {remoteParticipantSingle.role.name === 'Provider' &&
                      shouldShowDoctorTitle(remoteParticipantSingle.provider_profile?.credentials) && (
                        <span style={{ paddingRight: '4px' }}>Dr.</span>
                      )}
                    {remoteParticipantSingle.role.name !== UserRoleName.Dependent
                      ? remoteParticipantSingle.full_name
                      : remoteParticipantSingle.guardian.full_name}{' '}
                    {remoteParticipantSingle.role.name !== UserRoleName.Dependent &&
                      remoteParticipantSingle?.status === 'Deactivated' &&
                      '(Deactivated)'}
                  </span>
                </div>

                {isUserParticipant ? (
                  <span className={`fs-unmask ${styles.conversationType}`}>
                    {getFriendlyRoleName(remoteParticipantSingle.role.name)}
                    {isArchived && <span className={`fs-unmask ${styles.archivedBadge}`}>Archived</span>}
                  </span>
                ) : (
                  <span className={`fs-unmask ${styles.conversationType}`}>
                    Read Only
                    {isArchived && <span className={`fs-unmask ${styles.archivedBadge}`}>Archived</span>}
                    {isMobile && (
                      <div
                        className={styles.detailsIcon}
                        style={{ display: isSelected ? 'none' : 'block' }}
                        onClick={() => {
                          roleNavigate(`/case-chat/${currentCase?.id}/${item?.sid}`);
                        }}
                      >
                        <IoIosArrowDroprightCircle />
                      </div>
                    )}
                  </span>
                )}
                <div className={styles.relation}>
                  {remoteParticipantSingle.role.name === UserRoleName.Dependent && (
                    <span className="fs.exclude">
                      {`${remoteParticipantSingle.guardian_relationship} of `}
                      <span className={styles.relationSpan}>
                        {remoteParticipantSingle.full_name}{' '}
                        {remoteParticipantSingle?.status === 'Deactivated' && '(Deactivated)'}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.participantDivInfoRight}>
              <span className={styles.conversationSpecificInfoLastMessageTime}>
                {getTimeAgo(
                  item?.last_message?.updated_at
                    ? item?.last_message?.updated_at
                    : (item?.last_message?.created_at as string),
                  timeZone,
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
      {currentCase && (
        <div className={styles.conversationSpecificInfo}>
          {showCaseId && <span className={`fs-unmask ${styles.caseIdChip}`}>Case ID: {currentCase.id}</span>}
          <span className={styles.conversationSpecificInfoCaseInfo}>
            {getFriendlyNameOfCaseType(currentCase.type)}
            {(currentCase.type === CaseType.video_call_scheduled ||
              currentCase.type === CaseType.video_call_instant ||
              currentCase.type === CaseType.phone_call_scheduled) &&
              currentCase.video_conference_date && (
                <span className={`fs-unmask ${styles.currentCaseVideoDate}`}>
                  <span> on {getCaseDateWithoutDay(currentCase.video_conference_date, timeZone)}</span>
                  <span> at {getCaseTime(currentCase.video_conference_date, timeZone)}</span>
                </span>
              )}
          </span>
          <div className={styles.chatInfoFooter}>
            <span className={styles.conversationSpecificInfoLastMessage}>
              {item?.last_message?.body ? (
                // The preview's paragraph is block-level here, so the sender
                // label sits beside the message HTML instead of inside it.
                <div style={{ display: 'flex', maxWidth: '-webkit-fill-available' }} className="fs-exclude">
                  {isSenderYou && <span style={{ flexShrink: 0, paddingRight: '4px' }}>You:</span>}
                  <div
                    style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    dangerouslySetInnerHTML={{
                      __html: displayLastMessageContent(item.last_message.body),
                    }}
                  />
                </div>
              ) : item?.last_message?.media_ids && item?.last_message?.media_ids?.length > 0 ? (
                `${isSenderYou ? 'You: ' : ''}Media`
              ) : item?.last_message?.body === null && item?.last_message?.updated_at !== null ? (
                `${isSenderYou ? 'You: ' : ''}This message was deleted`
              ) : (
                ''
              )}
            </span>
            {userInfo.role.name === UserRoleName.Patient &&
              unreadCount > 0 &&
              ((conversationType === `${UserRoleName.Patient}_${UserRoleName.Provider}` &&
                currentCase &&
                hasUnreadMessageInPatientProvider(currentCase, currentCase?.provider_id)) ||
                (conversationType === `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}` &&
                  currentCase &&
                  hasUnreadMessageInPatientCA(currentCase, currentCase?.care_advisor_id))) && (
                <div className={styles.notificationUnreadCountPatient}>{unreadCount}</div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

CaseMessageInfo.defaultProps = {
  isMobile: false,
};
export default CaseMessageInfo;
